import BasePoint from "@/model/BasePoint";

export const getSpacing = (start: BasePoint, end: BasePoint): number => {
  //通过三角函数得到两点间的距离
  const x = end.x - start.x;
  const y = end.y - start.y;
  return Math.sqrt(x * x + y * y);
};

export const getTowPointCenterPoint = (
  start: BasePoint,
  end: BasePoint
): BasePoint => {
  //获取两点间中心点x
  const centerX = (start.x + end.x) / 2;
  const centerY = (start.y + end.y) / 2;
  return { x: centerX, y: centerY };
};

export const getAngle = (p1: BasePoint, p2: BasePoint): number => {
  //得到旋转角度，getAngle是得到夹角的一个方法
  const x = p1.x - p2.x;
  const y = p1.y - p2.y;
  return (Math.atan2(y, x) * 180) / Math.PI;
};

export const originPoint2LocalPoint = (
  //把真实坐标转换成画图的坐标
  x: number,
  y: number,
  leftBoundary: number, //真实地图的左下角为原点的坐标点x
  downBoundary: number, //真实地图的左下角为原点的坐标点y
  pixelExtent: number //像素比例 1像素代表的距离
): BasePoint => {
  //转换成本地画布上可用的点
  const px = (x - leftBoundary) / pixelExtent;
  const py = (y - downBoundary) / pixelExtent;
  const localX = Math.round(px * 100) / 100;
  const localY = Math.round(py * 100) / 100;

  // const localX = (x - leftBoundary) / pixelExtent;
  // const localY = (y - downBoundary) / pixelExtent;

  return { x: localX, y: localY };
};

export const translateLocalToOrigin = (
  //把画图的坐标转换成真实坐标
  x: number,
  y: number,
  leftBoundary: number, //真实地图的左下角为原点的坐标点x
  downBoundary: number, //真实地图的左下角为原点的坐标点y
  pixelExtent: number //像素比例 1像素代表的距离
): { originX: number; originY: number } => {
  //转换成本地画布上可用的点
  const originX = leftBoundary + x * pixelExtent;
  const originY = y * pixelExtent + downBoundary;

  return { originX, originY };
};

export const getPointBeforeCanvasZoomAndTranslation = (
  x: number,
  y: number,
  scaleCenterX: number,
  scaleCenterY: number,
  canvasTransX: number,
  canvasTransY: number,
  canvasScale: number
): BasePoint => {
  //获取屏幕选中的对应画布上缩放平移变换前的点的原始位置（即未缩放平移时的位置）。
  const anchorPoint = { x: x, y: y };
  const scaleCenterPoint = { x: scaleCenterX, y: scaleCenterY };
  const scapX = anchorPoint.x - scaleCenterPoint.x;
  const scapY = anchorPoint.y - scaleCenterPoint.y;

  const choosePointX = anchorPoint.x - canvasTransX;
  const choosePointY = anchorPoint.y - canvasTransY;
  const anchorPointChoosePoint = { x: choosePointX, y: choosePointY }; //锚点选中的点
  if (scapX == 0 && scapY == 0) {
    return anchorPointChoosePoint;
  }
  const slope = scapY / scapX; //斜率
  const spac = getSpacing(scaleCenterPoint, anchorPoint); //缩放的中心点与锚点的距离

  const primevalSpac = spac * (1 - 1 / canvasScale); //原始距离
  const alpa = Math.atan(slope);

  const primevalX = primevalSpac * Math.cos(alpa);
  const primevalY = primevalSpac * Math.sin(alpa);

  let mx;
  let my;

  if (scapX > 0) {
    mx = anchorPointChoosePoint.x - primevalX;
    my = anchorPointChoosePoint.y - primevalY;
  } else {
    mx = anchorPointChoosePoint.x + primevalX;
    my = anchorPointChoosePoint.y + primevalY;
  }

  const translationZoomLocation = { x: mx, y: my };
  return translationZoomLocation;
};

export const getPointBeforeCanvasRotate = (
  displayCenterX: number,
  displayCenterY: number,
  activePointX: number,
  activePointY: number,
  angle: number
): BasePoint => {
  //获取屏幕选中的对应画布上旋转变换前的点的原始位置（即未旋转时的位置）。
  const x1 = activePointX;
  const y1 = activePointY;
  const x2 = displayCenterX;
  const y2 = displayCenterY;
  const currentAngle = (-angle * Math.PI) / 180;
  const x =
    (x1 - x2) * Math.cos(currentAngle) -
    (y1 - y2) * Math.sin(currentAngle) +
    x2;
  const y =
    (x1 - x2) * Math.sin(currentAngle) +
    (y1 - y2) * Math.cos(currentAngle) +
    y2;
  return { x: x, y: y };
};

export const getPointAfterCanvasRotate = (
  afterPointX: number,
  afterPointY: number,
  displayCenterX: number,
  displayCenterY: number,
  angle: number
): BasePoint => {
  //获取屏幕对应画布上旋转变换后的点位置（即旋转后的位置）。
  const x1 = afterPointX;
  const y1 = afterPointY;
  const x2 = displayCenterX;
  const y2 = displayCenterY;

  const w = x2 - x1;
  const h = y2 - y1;
  // (Math.acos(cosM) * 180 / Math.PI * k);
  const tanAngle = (Math.atan2(h, w) * 180) / Math.PI;
  const curAngle = tanAngle + angle;
  const radius = Math.sqrt(w * w + h * h);
  const h2 = radius * Math.sin((Math.PI / 180) * curAngle);
  const w2 = radius * Math.cos((Math.PI / 180) * curAngle);
  const x = x2 - w2;
  const y = y2 - h2;
  return { x: x, y: y };
};

export const getPointBeforeCanvasTransformation = (
  x: number,
  y: number,
  scaleCenterX: number,
  scaleCenterY: number,
  canvasTransX: number,
  canvasTransY: number,
  canvasScale: number,
  displayCenterX: number,
  displayCenterY: number,
  canvasRotate: number
): BasePoint => {
  //获取选中的点对于画布缩放平移旋转变换前的点的原始坐标（即未缩放平移时的点坐标）。
  const currentTranslationZoomPoint = getPointBeforeCanvasZoomAndTranslation(
    x,
    y,
    scaleCenterX,
    scaleCenterY,
    canvasTransX,
    canvasTransY,
    canvasScale
  );
  const currentRotatePoint = getPointBeforeCanvasRotate(
    displayCenterX,
    displayCenterY,
    currentTranslationZoomPoint.x,
    currentTranslationZoomPoint.y,
    canvasRotate
  );
  return currentRotatePoint;
};
